import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { theme } from "../theme";
import DashboardCard from "./common/DashboardCard";
import SkeletonCard from "./common/SkeletonCard";
import EmptyState from "./common/EmptyState";
import { API_ENDPOINTS } from "../api/config";
import { formatApiError, logError } from "../api/errorHandler";

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const formatMoney = (value, currency) => {
  const num = typeof value === "number" ? value : Number(value || 0);
  return `${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
};

const formatPercent = (value) => {
  const num = typeof value === "number" ? value : Number(value || 0);
  return `${num.toFixed(1)}%`;
};

const formatDateOnly = (value) => {
  if (!value) return "";
  const str = String(value);
  return str.length >= 10 ? str.slice(0, 10) : str;
};

const DirectionBadge = ({ direction }) => {
  const tone = (direction || "FLAT").toUpperCase();
  const colorMap = {
    UP: "#10b981",
    DOWN: "#ef4444",
    FLAT: "#94a3b8",
  };
  return (
    <span style={{
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: "12px",
      fontWeight: 700,
      background: `${colorMap[tone] || colorMap.FLAT}22`,
      color: colorMap[tone] || colorMap.FLAT,
      border: `1px solid ${colorMap[tone] || colorMap.FLAT}`,
    }}>
      {tone}
    </span>
  );
};

const ComparisonCard = ({ title, data, currency }) => {
  if (!data) return null;
  const diff = Number(data.diff_value || 0);
  const diffLabel = diff > 0 ? `+${formatMoney(diff, currency)}` : formatMoney(diff, currency);
  return (
    <div style={{
      background: theme.colors.background.card,
      borderRadius: theme.borderRadius.md,
      padding: theme.spacing.lg,
      border: `1px solid ${theme.colors.border.default}`,
      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
      display: "flex",
      flexDirection: "column",
      gap: "10px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: "18px", color: theme.colors.text.primary }}>{title}</h3>
        <DirectionBadge direction={data.direction} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        <div>
          <div style={{ fontSize: "12px", color: theme.colors.text.muted }}>Current</div>
          <div style={{ fontSize: "18px", fontWeight: 700, color: theme.colors.text.primary }}>
            {formatMoney(data.current_value, currency)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: "12px", color: theme.colors.text.muted }}>Previous</div>
          <div style={{ fontSize: "18px", fontWeight: 700, color: theme.colors.text.primary }}>
            {formatMoney(data.previous_value, currency)}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: "14px", color: theme.colors.text.secondary }}>
          Diff: <strong style={{ color: theme.colors.text.primary }}>{diffLabel}</strong>
        </div>
        <div style={{ fontSize: "14px", color: theme.colors.text.secondary }}>
          {formatPercent(data.diff_percent)}
        </div>
      </div>
    </div>
  );
};

const FocusChips = ({ items = [] }) => {
  if (!items.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "10px" }}>
      {items.map((code) => (
        <span key={code} style={{
          padding: "6px 10px",
          borderRadius: 999,
          fontSize: "12px",
          fontWeight: 600,
          color: theme.colors.primary,
          border: `1px solid ${theme.colors.primary}`,
          background: "rgba(249,115,22,0.08)",
        }}>
          {code}
        </span>
      ))}
    </div>
  );
};

const PipelineFocusSummary = ({ rows = [], currency }) => {
  const data = useMemo(() => {
    const list = [...(rows || [])].map((r) => ({
      stage: r.stage || "Unknown",
      count: Number(r.count || 0),
      value: Number(r.value || 0),
    }));
    list.sort((a, b) => b.value - a.value);
    return list;
  }, [rows]);

  const totalValue = data.reduce((acc, r) => acc + r.value, 0);
  const totalCount = data.reduce((acc, r) => acc + r.count, 0);
  const topByValue = data.slice(0, 3);
  const topByCount = [...data].sort((a, b) => b.count - a.count)[0];
  const largestStage = topByValue[0];
  const concentrationValuePct = totalValue > 0 ? (largestStage.value / totalValue) * 100 : 0;
  const concentrationCountPct = totalCount > 0 ? (topByCount.count / totalCount) * 100 : 0;

  const stuckStages = data.filter((r) =>
    ["Quote Sent", "Quote Ready", "Estimation/BOQ"].includes(r.stage)
  );
  const zeroValueStages = data.filter((r) => r.count > 0 && r.value === 0);

  const sentences = [];
  if (totalValue === 0 && totalCount === 0) {
    sentences.push("No pipeline data available for this selection yet. Try a different salesperson or year.");
  } else if (topByValue.length > 0) {
    sentences.push(
      `The biggest value is concentrated in ${topByValue.map((s) => `${s.stage} (${formatMoney(s.value, currency)})`).join(", ")}.`
    );
  }

  if (!(totalValue === 0 && totalCount === 0)) {
    const concentrationRisk = concentrationValuePct >= 45 || concentrationCountPct >= 45;
    if (concentrationRisk && largestStage && topByCount) {
      sentences.push(
        `This is a heavy concentration: ${largestStage.stage} alone carries ${concentrationValuePct.toFixed(1)}% of value, and ${topByCount.stage} holds ${concentrationCountPct.toFixed(1)}% of deal volume.`
      );
    }

    if (stuckStages.length > 0) {
      const stuckSummary = stuckStages
        .map((s) => `${s.stage} (${formatMoney(s.value, currency)})`)
        .join(", ");
      sentences.push(
        `A sizable share is sitting in late stages like ${stuckSummary}. These are the most likely to slip without proactive follow-ups.`
      );
    }

    if (zeroValueStages.length > 0) {
      sentences.push(
        `Some stages have activity but no recorded value (${zeroValueStages.map((s) => s.stage).join(", ")}). This can hide risk and needs a quick data check.`
      );
    }

    if (sentences.length === 0) {
      sentences.push(
        "Your pipeline looks balanced right now with no obvious bottlenecks. Keep momentum by pushing the top stages forward."
      );
    }
  }

  return (
    <div style={{
      background: theme.colors.background.card,
      borderRadius: theme.borderRadius.md,
      padding: theme.spacing.lg,
      border: `1px solid ${theme.colors.border.default}`,
      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    }}>
      <h3 style={{ margin: 0, fontSize: "22px", fontWeight: 700, color: theme.colors.text.primary }}>
        Pipeline Focus Summary
      </h3>
      <div style={{ marginTop: "12px", color: theme.colors.text.secondary, lineHeight: 1.8, fontSize: "16px" }}>
        {sentences.map((s, idx) => (
          <div key={`pf-${idx}`} style={{ marginBottom: "10px" }}>{s}</div>
        ))}
      </div>
      <div style={{ marginTop: "16px" }}>
        <div style={{ fontSize: "16px", fontWeight: 700, color: theme.colors.text.primary, marginBottom: "10px" }}>
          Suggested actions
        </div>
        <ul style={{ margin: 0, paddingLeft: "18px", color: theme.colors.text.secondary, fontSize: "15px", lineHeight: 1.8 }}>
          <li>Focus on moving the top-value stage forward within 14 days.</li>
          <li>Review Quote Sent / Quote Ready / Estimation deals for next-step blockers.</li>
          <li>Validate any stages with counts but zero value for data quality issues.</li>
        </ul>
      </div>
    </div>
  );
};

const ForecastBookingSummary = ({ items = [], focusOn = [], currency, previousMonth = null, currentMonthName = "" }) => {
  const parsed = items.map((item) => ({
    ...item,
    code: item.opportunity_code || "N/A",
    days: typeof item.days_remaining === "number" ? item.days_remaining : Number(item.days_remaining),
    valueNum: Number(item.value || 0),
    dateStr: formatDateOnly(item.expected_close),
  }));

  const overdue = parsed.filter((i) => Number.isFinite(i.days) && i.days < 0);
  const dueToday = parsed.filter((i) => Number.isFinite(i.days) && i.days === 0);
  const dueSoon = parsed.filter((i) => Number.isFinite(i.days) && i.days > 0 && i.days <= 3);

  const pickCodes = (list, max) => {
    const out = [];
    for (const item of list) {
      if (!item.code) continue;
      if (!out.includes(item.code)) out.push(item.code);
      if (out.length >= max) break;
    }
    return out;
  };

  const formatCodesWithMore = (codes, total) => {
    if (codes.length === 0) return "";
    const more = total - codes.length;
    return more > 0 ? `${codes.join(", ")} +${more} more` : codes.join(", ");
  };

  const overdueCodes = pickCodes(overdue, 10);
  const dueSoonCodes = pickCodes([...dueToday, ...dueSoon], 10);

  const highest = parsed.reduce((best, cur) => (cur.valueNum > (best?.valueNum || 0) ? cur : best), null);

  const earlyStages = ["Quote Sent", "Quote Ready", "Estimation/BOQ"];
  const early = parsed.filter((i) => earlyStages.includes(i.stage));
  const closeReadyStages = ["Negotiation", "Approved"];
  const closeReady = parsed.filter((i) => closeReadyStages.includes(i.stage));
  const closeReadyCodes = pickCodes(closeReady, 8);

  const focusCodes = [];
  for (const c of overdueCodes) {
    if (!focusCodes.includes(c)) focusCodes.push(c);
  }
  for (const c of dueSoonCodes) {
    if (!focusCodes.includes(c)) focusCodes.push(c);
  }
  for (const c of focusOn || []) {
    if (focusCodes.length >= 15) break;
    if (c && !focusCodes.includes(c)) focusCodes.push(c);
  }
  for (const item of parsed) {
    if (focusCodes.length >= 15) break;
    if (item.code && !focusCodes.includes(item.code)) focusCodes.push(item.code);
  }

  const currentMonthChips = (items || []).map((item) => item.opportunity_code).filter(Boolean);
  const previousMonthChips = (previousMonth?.items || []).map((item) => item.opportunity_code).filter(Boolean);

  const sentences = [];
  if (overdue.length > 0) {
    sentences.push({
      text: `You have ${overdue.length} overdue opportunities that already missed their expected close date: ${formatCodesWithMore(overdueCodes, overdue.length)}. These need immediate follow-up today.`,
      color: "#ef4444",
    });
  } else {
    sentences.push({
      text: "Good news — none of this month's opportunities are overdue.",
      color: "#10b981",
    });
  }

  if ((dueToday.length + dueSoon.length) > 0) {
    sentences.push({
      text: `${dueToday.length + dueSoon.length} opportunities are due within the next 3 days: ${formatCodesWithMore(dueSoonCodes, dueToday.length + dueSoon.length)}. Prioritize these to avoid month-end slippage.`,
      color: "#f97316",
    });
  }

  if (highest) {
    sentences.push({
      text: `The biggest opportunity to protect this month is ${highest.code} (${formatMoney(highest.valueNum, currency)}) in ${highest.stage || "Unknown"}, closing on ${highest.dateStr || "N/A"}.`,
      color: theme.colors.text.secondary,
    });
  }

  if (early.length > 0) {
    sentences.push({
      text: "A large portion of this month’s forecast is still sitting in early stages (Quote Sent / Quote Ready / Estimation). Push follow-ups and next steps to prevent delays.",
      color: theme.colors.text.secondary,
    });
  }

  if (closeReady.length > 0) {
    sentences.push({
      text: `Close-ready deals are sitting in Negotiation/Approved: ${formatCodesWithMore(closeReadyCodes, closeReady.length)}. These are your best quick wins.`,
      color: theme.colors.text.secondary,
    });
  }

  return (
    <div style={{
      background: theme.colors.background.card,
      borderRadius: theme.borderRadius.md,
      padding: theme.spacing.lg,
      border: `1px solid ${theme.colors.border.default}`,
      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    }}>
      <h3 style={{ margin: 0, fontSize: "22px", fontWeight: 700, color: theme.colors.text.primary }}>
        Forecasted Booking (This Month) – Focus Summary
      </h3>

      <div style={{ marginTop: "12px", lineHeight: 1.8, fontSize: "16px" }}>
        {sentences.map((s, idx) => (
          <div key={`fb-${idx}`} style={{ marginBottom: "10px", color: s.color }}>
            {s.text}
          </div>
        ))}
      </div>

      {currentMonthChips.length > 0 && (
        <div style={{ marginTop: "12px", fontSize: "15px", color: theme.colors.text.secondary }}>
          <div style={{ fontWeight: 600, marginBottom: "6px", color: theme.colors.text.primary }}>
            Current Month{currentMonthName ? ` (${currentMonthName})` : ""}:
          </div>
          <FocusChips items={currentMonthChips} />
        </div>
      )}
      {previousMonthChips.length > 0 && (
        <div style={{ marginTop: "12px", fontSize: "15px", color: theme.colors.text.secondary }}>
          <div style={{ fontWeight: 600, marginBottom: "6px", color: theme.colors.text.primary }}>
            Previous Months (Still Open):
          </div>
          <FocusChips items={previousMonthChips} />
        </div>
      )}

      {focusCodes.length > 0 && (
        <div style={{ marginTop: "16px" }}>
          <div style={{ fontSize: "16px", fontWeight: 700, color: theme.colors.text.primary, marginBottom: "10px" }}>
            Focus on
          </div>
          <FocusChips items={focusCodes} />
        </div>
      )}

      <div style={{ marginTop: "14px" }}>
        <div style={{ fontSize: "16px", fontWeight: 700, color: theme.colors.text.primary, marginBottom: "10px" }}>
          Recommended actions
        </div>
        <ul style={{ margin: 0, paddingLeft: "18px", color: theme.colors.text.secondary, fontSize: "15px", lineHeight: 1.8 }}>
          <li>Follow up on overdue deals immediately.</li>
          <li>Prioritize deals closing within the next 3 days.</li>
          <li>Push Negotiation/Approved deals to closure and re-confirm early-stage deals.</li>
        </ul>
      </div>
    </div>
  );
};

const PriorityFocusSummary = ({ items = [], currency, focusOn = [] }) => {
  const today = new Date();

  const parsed = items.map((item) => {
    const dateStr = formatDateOnly(item.expected_close);
    const dateObj = dateStr ? new Date(dateStr) : null;
    let daysToClose = null;
    if (dateObj && !Number.isNaN(dateObj.getTime())) {
      const diffMs = dateObj.getTime() - today.getTime();
      daysToClose = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    }
    return { ...item, dateStr, daysToClose };
  });

  const urgent = parsed.filter((i) => i.daysToClose !== null && i.daysToClose <= 7);
  const closeReady = parsed.filter((i) => ["Negotiation", "Approved"].includes(i.stage));

  const values = parsed.map((i) => Number(i.value || 0)).sort((a, b) => b - a);
  const thresholdIndex = Math.max(0, Math.floor(values.length * 0.25) - 1);
  const highValueThreshold = values[thresholdIndex] || 0;
  const earlyStages = ["Quote Sent", "Estimation/BOQ", "Quote Ready"];
  const highValueEarly = parsed.filter(
    (i) => earlyStages.includes(i.stage) && Number(i.value || 0) >= highValueThreshold
  );

  const collectCodes = (list, max) => {
    const out = [];
    for (const item of list) {
      if (!item.opportunity_code) continue;
      if (!out.includes(item.opportunity_code)) out.push(item.opportunity_code);
      if (out.length >= max) break;
    }
    return out;
  };

  const urgentCodes = collectCodes(urgent, 25);
  const closeReadyCodes = collectCodes(closeReady, 25);
  const highValueEarlyCodes = collectCodes(highValueEarly, 25);

  const focusCodes = [];
  for (const code of focusOn) {
    if (code && !focusCodes.includes(code)) focusCodes.push(code);
  }
  for (const item of parsed) {
    if (focusCodes.length >= 15) break;
    if (item.opportunity_code && !focusCodes.includes(item.opportunity_code)) {
      focusCodes.push(item.opportunity_code);
    }
  }

  const formatCodesLine = (codes, total) => {
    if (codes.length === 0) return "";
    const more = total - codes.length;
    return more > 0 ? `${codes.join(", ")} +${more} more` : codes.join(", ");
  };

  const sentences = [];
  if (urgentCodes.length > 0) {
    sentences.push({
      text: `You have urgent opportunities closing within 7 days: ${formatCodesLine(urgentCodes, urgent.length)}. These are the most time-sensitive items to protect.`,
      color: "#ef4444",
    });
  }

  if (highValueEarlyCodes.length > 0) {
    sentences.push({
      text: `High-value deals are still early-stage (Quote Sent / Quote Ready / Estimation): ${formatCodesLine(highValueEarlyCodes, highValueEarly.length)}. These need next-step nudges now to avoid slipping.`,
      color: theme.colors.text.secondary,
    });
  }

  if (sentences.length === 0) {
    sentences.push({
      text: "No urgent or early-stage high-value risks right now. This is a good window to accelerate close-ready deals.",
      color: "#10b981",
    });
  }

  if (closeReadyCodes.length > 0) {
    sentences.push({
      text: `Close-ready opportunities in Negotiation/Approved: ${formatCodesLine(closeReadyCodes, closeReady.length)}. These are the fastest wins to push this week.`,
      color: theme.colors.text.secondary,
    });
  }

  return (
    <div style={{
      background: theme.colors.background.card,
      borderRadius: theme.borderRadius.md,
      padding: theme.spacing.lg,
      border: `1px solid ${theme.colors.border.default}`,
      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    }}>
      <h3 style={{ margin: 0, fontSize: "22px", fontWeight: 700, color: theme.colors.text.primary }}>
        Priority Focus Summary
      </h3>
      <div style={{ marginTop: "12px", color: theme.colors.text.secondary, lineHeight: 1.8, fontSize: "16px" }}>
        {sentences.map((s, idx) => (
          <div key={`prio-${idx}`} style={{ marginBottom: "10px", color: s.color }}>
            {s.text}
          </div>
        ))}
      </div>
      {focusCodes.length > 0 && (
        <div style={{ marginTop: "16px" }}>
          <div style={{ fontSize: "16px", fontWeight: 700, color: theme.colors.text.primary, marginBottom: "10px" }}>
            Focus on
          </div>
          <FocusChips items={focusCodes} />
        </div>
      )}
    </div>
  );
};

function AnomalyAssistant({ filterContext, onMaximize, fullScreen = false }) {
  const { salesperson, year, region } = filterContext || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!salesperson || !year) return;
    const run = async () => {
      try {
        setLoading(true);
        setError("");
        setData(null);

        const csrfToken = localStorage.getItem("csrf_token");
        const headers = {};
        if (csrfToken) {
          headers["X-CSRF-Token"] = csrfToken;
        }

        const response = await axios.post(
          API_ENDPOINTS.anomalyDashboard,
          {
            salesperson,
            year: parseInt(year, 10),
            region: region || "all",
          },
          {
            timeout: 120000,
            withCredentials: true,
            headers: { "Content-Type": "application/json", ...headers },
          }
        );

        setData(response.data);
      } catch (err) {
        logError(err, "AnomalyAssistant");
        const info = formatApiError(err);
        setError(`${info.message}: ${info.details}`);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [salesperson, year, region]);

  const context = data?.context;
  const currency = context?.currency?.code || "AED";
  const monthName = context?.current_month ? MONTH_NAMES[context.current_month - 1] : "";
  const subtitle = context
    ? `${context.salesperson} · ${context.selected_year} · ${monthName} · Region: ${context.region}`
    : "";

  const actionButtons = useMemo(() => (
    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
      {onMaximize && !fullScreen && (
        <button
          onClick={onMaximize}
          style={{
            padding: "8px",
            background: "rgba(255, 255, 255, 0.05)",
            color: "#e5e7eb",
            borderRadius: 8,
            border: "1px solid rgba(255, 255, 255, 0.1)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title="Maximize"
        >
          <span style={{ fontSize: "16px", lineHeight: 1 }}>⤢</span>
        </button>
      )}
    </div>
  ), [onMaximize, fullScreen]);

  return (
    <DashboardCard title="Anomaly Assistant" actions={actionButtons} fullScreen={fullScreen} gradient="warning">
      {context && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", marginBottom: "18px" }}>
          <div>
            <div style={{ fontSize: "14px", color: theme.colors.text.secondary }}>{subtitle}</div>
          </div>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <span style={{
              padding: "6px 10px",
              borderRadius: 999,
              fontSize: "12px",
              fontWeight: 700,
              border: `1px solid ${theme.colors.primary}`,
              color: theme.colors.primary,
              background: "rgba(249,115,22,0.08)",
            }}>
              {currency}
            </span>
            <span style={{ fontSize: "12px", color: theme.colors.text.muted }}>
              {context.generated_at}
            </span>
          </div>
        </div>
      )}

      {!context && !loading && !error && (
        <EmptyState icon="" message="Select a salesperson and year to load anomaly insights." size="medium" />
      )}

      {loading && (
        <div style={{ display: "grid", gridTemplateColumns: fullScreen ? "repeat(2, minmax(0, 1fr))" : "repeat(auto-fit, minmax(320px, 1fr))", gap: theme.spacing.lg }}>
          <SkeletonCard height="180px" />
          <SkeletonCard height="180px" />
          <SkeletonCard height="280px" />
          <SkeletonCard height="280px" />
        </div>
      )}

      {error && (
        <div style={{
          background: "rgba(239, 68, 68, 0.1)",
          borderRadius: theme.borderRadius.sm,
          border: `1px solid ${theme.colors.danger}`,
          color: "#fca5a5",
          padding: theme.spacing.md,
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {data && (
          <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing.lg }}>
            <div style={{ display: "grid", gridTemplateColumns: fullScreen ? "repeat(2, minmax(0, 1fr))" : "repeat(auto-fit, minmax(320px, 1fr))", gap: theme.spacing.lg }}>
              <ComparisonCard title="Month-wise Comparison (MoM)" data={data.month_wise_comparison} currency={currency} />
              <ComparisonCard title="Same Month Last Year (YoY)" data={data.same_month_last_year_comparison} currency={currency} />
            </div>

          <PipelineFocusSummary rows={data.forecast_pipeline_overview || []} currency={currency} />

          <PriorityFocusSummary
            items={data.high_priority_enquiries?.items || []}
            focusOn={data.high_priority_enquiries?.focus_on || []}
            currency={currency}
          />

          <ForecastBookingSummary
            items={data.forecasted_booking_this_month?.items || []}
            focusOn={data.forecasted_booking_this_month?.focus_on || []}
            currency={currency}
            currentMonth={context?.current_month}
            previousMonth={data.forecasted_booking_previous_months || data.forecast_previous_months}
            currentMonthName={monthName}
          />
        </div>
      )}
    </DashboardCard>
  );
}

export default AnomalyAssistant;
